import { useEffect, useRef, useState, useMemo } from 'react'

interface AnimatedGradientProps {
  colors?: string[]
  speed?: number
  blur?: 'light' | 'medium' | 'heavy'
  className?: string
}

function useDimensions(ref: React.RefObject<HTMLDivElement | null>) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  useEffect(() => {
    const el = ref.current
    if (!el) return

    let timeout: ReturnType<typeof setTimeout>
    const observer = new ResizeObserver((entries) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }, 250)
    })

    observer.observe(el)
    // Initial measurement
    const { width, height } = el.getBoundingClientRect()
    setDimensions({ width, height })

    return () => {
      clearTimeout(timeout)
      observer.disconnect()
    }
  }, [ref])

  return dimensions
}

const blurMap = {
  light: 'blur-2xl',
  medium: 'blur-3xl',
  heavy: 'blur-[100px]',
}

export function AnimatedGradient({
  colors = ['#2b6cee', '#10b981', '#f59e0b', '#ef4444'],
  speed = 5,
  blur = 'medium',
  className = '',
}: AnimatedGradientProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const { width, height } = useDimensions(containerRef)

  const circleSize = Math.min(width, height) * 0.45

  const circles = useMemo(() => {
    return colors.map((color, i) => ({
      color,
      cx: Math.random() * width * 0.5,
      cy: Math.random() * height * 0.5,
      delay: -(i * (speed / colors.length)),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors.length, width, height, speed])

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden ${className}`}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        xmlns="http://www.w3.org/2000/svg"
        className={`absolute inset-0 w-full h-full ${blurMap[blur]}`}
      >
        {circles.map((circle, i) => (
          <circle
            key={i}
            cx={circle.cx}
            cy={circle.cy}
            r={circleSize}
            fill={circle.color}
            opacity={0.3}
            className="dark:opacity-15"
          >
            <animateTransform
              attributeName="transform"
              type="translate"
              values={`0,0; ${width * 0.3},${height * 0.2}; ${width * 0.5},${height * 0.5}; ${width * 0.2},${height * 0.6}; ${width * 0.4},${height * 0.1}; 0,0`}
              dur={`${speed + i * 2}s`}
              repeatCount="indefinite"
              calcMode="spline"
              keySplines="0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1; 0.4 0 0.2 1"
              begin={`${circle.delay}s`}
            />
          </circle>
        ))}
      </svg>
    </div>
  )
}
